import { useCallback, useEffect, useMemo, useState } from "react"
import { AppUIContext } from "./AppUIContext"
import config from "config"
import { useNavigationHistory } from "./hooks"
import { useAppNavigation } from "./hooks/useAppNavigation/useAppNavigation"
import { useVenueController } from "hooks/venue/useVenueController"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import {
  removeBodyWheelchairClass,
  addBodyWheelchairClass,
} from "utils/wheelchair"
import { useWheelchairModeConfig } from "hooks/accessibility"
import { useTranslation } from "providers/i18n"
import { getDirectionFromLanguage } from "utils/theme"

export const AppUIProvider = ({ children }) => {
  const { i18n } = useTranslation()
  const { initVenueController } = useVenueController()
  const [openSearchState, setOpenSearchState] = useState(false)
  const [sidebarState, setSidebarState] = useState(false)
  const [mapLayout, setMapLayout] = useState("full")

  const { isWheelchairModeLocked } = useWheelchairModeConfig()
  const [isWheelchairModeEnabled, setIsWheelchairModeEnabled] = useState(
    isWheelchairModeLocked
  )

  const [themeDirection, setThemeDirection] = useState(
    getDirectionFromLanguage(i18n.language)
  )

  const { mapLoading, mapLoaded: mapReady } = useIndoorMap()

  const { goBack } = useNavigationHistory()
  const {
    mainMenus,
    sideMenus,
    matchedPath,
    matchedRouteOption,
    routeOptions,
    updateRouteOptions,
  } = useAppNavigation()

  const openSearch = useCallback(() => {
    setOpenSearchState(true)
  }, [])

  const closeSearch = useCallback(() => {
    setOpenSearchState(false)
  }, [])

  const toggleSearch = useCallback(() => {
    setOpenSearchState((prev) => !prev)
  }, [])

  const isKioskMode = useMemo(
    () => ["kiosk-v", "kiosk-h"].includes(config("device")),
    []
  )

  const setWheelchairModeEnabled = useCallback(
    (isEnabled) => {
      if (isWheelchairModeLocked) return
      setIsWheelchairModeEnabled(isEnabled)
    },
    [isWheelchairModeLocked]
  )

  useEffect(() => {
    const newDirection = getDirectionFromLanguage(i18n.language)
    document.documentElement.setAttribute("dir", newDirection)
    setThemeDirection(newDirection)
  }, [i18n.language])

  // Disable right click on the screen when application is in kiosk mode
  useEffect(() => {
    if (config("env") === "production" && isKioskMode) {
      window.addEventListener("contextmenu", (event) => event.preventDefault())
    }
  }, [isKioskMode])

  useEffect(() => {
    initVenueController()
  }, [initVenueController])

  useEffect(() => {
    if (isWheelchairModeLocked) {
      setIsWheelchairModeEnabled(true)
    }
  }, [isWheelchairModeLocked])

  useEffect(() => {
    isWheelchairModeEnabled
      ? addBodyWheelchairClass()
      : removeBodyWheelchairClass()
  }, [isWheelchairModeEnabled])

  const value = {
    mapLayout,
    setMapLayout,
    openSearchState,
    sidebarState,
    mapReady,
    mapLoading,
    isKioskMode,
    isWheelchairModeEnabled,
    themeDirection,
    // Navigation
    mainMenus,
    sideMenus,
    matchedPath,
    matchedRouteOption,
    routeOptions,
    // functions
    openSearch,
    closeSearch,
    toggleSearch,
    setOpenSearchState,
    setSidebarState,
    goBack,
    setWheelchairModeEnabled,
    updateRouteOptions,
  }

  return <AppUIContext.Provider value={value}>{children}</AppUIContext.Provider>
}
